// Settings → Stock control (D-049, D-100, D-046/B-033): when a count or write-off needs a
// manager (write-offs from R…, count adjustments from …% or R…), when the daily expiry list
// is made, and how long a station waits before signing its person out. Changing them needs
// "approve write-offs" and access to every branch (D-049); anyone who can see reports can
// read them.

import type { StockControlSettings, UpdateStockControlSettings } from '@enp/domain/api';
import { MAX_STATION_AUTO_SIGN_OUT_MINUTES } from '@enp/domain/devices';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Card,
  FormField,
  Input,
  LoadingSkeleton,
  PageHeader,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Save, Undo2 } from 'lucide-react';
import {
  useState,
  type ChangeEvent,
  type ComponentProps,
  type FormEvent,
  type ReactNode,
} from 'react';
import { meQueryOptions } from '../../../auth/session.js';
import { useDocumentTitle } from '../../../shell/page.js';
import { ErrorPanel } from '../../../shell/route-states.js';
import { Notice, SettingsFrame } from '../../../stations/page-parts.js';
import { actionProblem } from '../../../stock-control/parts.js';
import {
  invalidateStockControl,
  stockControlSettingsQuery,
  updateStockControlSettings,
} from '../../../stock-control/queries.js';
import { parsePercent, parseRand, randInput, signOutText } from '../../../stock-control/words.js';

export const Route = createFileRoute('/_app/settings/stock-control')({
  component: StockControlSettingsPage,
});

interface Draft {
  writeoff: string;
  countPct: string;
  countRand: string;
  listTime: string;
  signOut: string;
}

const toDraft = (s: StockControlSettings): Draft => ({
  writeoff: randInput(s.writeoff_min_cents),
  countPct: String(s.count_adjust_pct),
  countRand: randInput(s.count_adjust_min_cents),
  listTime: s.state_list_time,
  signOut: String(s.station_auto_sign_out_minutes),
});

type Errors = Partial<Record<keyof Draft, string>>;

/** The draft checked: the changes to send, or what is wrong with each field. */
function checkDraft(
  draft: Draft,
  saved: StockControlSettings,
): { changes: UpdateStockControlSettings; errors: Errors } {
  const errors: Errors = {};
  const changes: UpdateStockControlSettings = {};
  const writeoff = parseRand(draft.writeoff);
  if (writeoff === null) errors.writeoff = 'Enter an amount in rand, like 250 or 250.00.';
  else if (writeoff !== saved.writeoff_min_cents) changes.writeoff_min_cents = writeoff;
  const pct = parsePercent(draft.countPct);
  if (pct === null) errors.countPct = 'Enter a percentage above 0 and up to 100, like 10.';
  else if (pct !== saved.count_adjust_pct) changes.count_adjust_pct = pct;
  const countRand = parseRand(draft.countRand);
  if (countRand === null) errors.countRand = 'Enter an amount in rand, like 250 or 250.00.';
  else if (countRand !== saved.count_adjust_min_cents) changes.count_adjust_min_cents = countRand;
  const time = draft.listTime.trim();
  const m = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) {
    errors.listTime = 'Enter a 24-hour time, like 04:00.';
  } else {
    const hhmm = `${m[1]?.padStart(2, '0')}:${m[2]}`;
    if (hhmm !== saved.state_list_time) changes.state_list_time = hhmm;
  }
  const signOut = draft.signOut.trim();
  const minutes = /^\d{1,3}$/.test(signOut) ? Number(signOut) : NaN;
  if (!Number.isInteger(minutes) || minutes > MAX_STATION_AUTO_SIGN_OUT_MINUTES) {
    errors.signOut = `Enter whole minutes from 0 to ${MAX_STATION_AUTO_SIGN_OUT_MINUTES}; 0 means never.`;
  } else if (minutes !== saved.station_auto_sign_out_minutes) {
    changes.station_auto_sign_out_minutes = minutes;
  }
  return { changes, errors };
}

function Section({
  id,
  title,
  description,
  children,
}: {
  id: string;
  title: string;
  description: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="gap-4 p-5">
      <div className="flex flex-col gap-1">
        <h2 id={id} className="font-display text-lg font-semibold text-fg">
          {title}
        </h2>
        <p className="text-body text-muted">{description}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

function RandInput(props: ComponentProps<typeof Input>) {
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-body text-muted"
      >
        R
      </span>
      <Input inputMode="decimal" autoComplete="off" className="pl-7 tabular-nums" {...props} />
    </div>
  );
}

function SettingsForm({ saved, canEdit }: { saved: StockControlSettings; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => toDraft(saved));
  const [errors, setErrors] = useState<Errors>({});
  const save = useMutation({
    mutationFn: (changes: UpdateStockControlSettings) => updateStockControlSettings(changes),
    onSuccess: async (next) => {
      queryClient.setQueryData(stockControlSettingsQuery().queryKey, next);
      await invalidateStockControl(queryClient);
      setDraft(toDraft(next));
      toast.success('Stock control settings saved', {
        description: 'Stations pick up the new limits and sign-out time when they next sync.',
      });
    },
  });
  const { changes } = checkDraft(draft, saved);
  const dirty =
    Object.keys(changes).length > 0 || JSON.stringify(draft) !== JSON.stringify(toDraft(saved));
  const failed = save.isError ? actionProblem(save.error, 'The settings weren’t saved') : null;

  const set = (key: keyof Draft) => (e: ChangeEvent<HTMLInputElement>) => {
    setDraft((d) => ({ ...d, [key]: e.target.value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const checked = checkDraft(draft, saved);
    setErrors(checked.errors);
    if (Object.keys(checked.errors).length > 0) {
      const first = Object.keys(checked.errors)[0];
      document.querySelector<HTMLInputElement>(`[data-field="${first}"]`)?.focus();
      return;
    }
    if (Object.keys(checked.changes).length === 0) {
      toast.success('Nothing to save', { description: 'These are the settings already in use.' });
      return;
    }
    save.mutate(checked.changes);
  };
  const minutes = Number(draft.signOut);

  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex flex-col gap-5"
      aria-label="Stock control settings"
    >
      <fieldset disabled={!canEdit || save.isPending} className="flex flex-col gap-5">
        <legend className="sr-only">Stock control settings</legend>
        <Section
          id="approvals"
          title="Manager approval"
          description="A count or write-off above these limits needs a manager: a PIN at the station, or a decision in Approvals. It is recorded either way."
        >
          <FormField
            label="Write-offs worth at or above (R)"
            error={errors.writeoff}
            description="At standard cost. Default R 250.00."
          >
            <RandInput data-field="writeoff" value={draft.writeoff} onChange={set('writeoff')} />
          </FormField>
          <div className="hidden sm:block" />
          <FormField
            label="Count changes at or above (%)"
            error={errors.countPct}
            description="Of the level before the count, per product. Default 10%."
          >
            <Input
              data-field="countPct"
              inputMode="decimal"
              autoComplete="off"
              className="tabular-nums"
              value={draft.countPct}
              onChange={set('countPct')}
            />
          </FormField>
          <FormField
            label="Or count changes worth at or above (R)"
            error={errors.countRand}
            description="Either one is enough to need a manager. A product without a cost is judged by % only. Default R 250.00."
          >
            <RandInput data-field="countRand" value={draft.countRand} onChange={set('countRand')} />
          </FormField>
        </Section>

        <Section
          id="expiry-list"
          title="Daily expiry list"
          description="Each morning, per branch, the list of lots that change expiry state that day is made at this time (branch time, 24-hour)."
        >
          <FormField label="Make the list at" error={errors.listTime} description="Default 04:00.">
            <Input
              data-field="listTime"
              inputMode="numeric"
              autoComplete="off"
              placeholder="04:00"
              className="w-32 tabular-nums"
              value={draft.listTime}
              onChange={set('listTime')}
            />
          </FormField>
        </Section>

        <Section
          id="stations"
          title="Stations"
          description="A shared tablet signs its person out after this long without a touch, so the next person never works under someone else’s name."
        >
          <FormField
            label="Sign out after (minutes)"
            error={errors.signOut}
            description={`0 means never. Default 10. Now: ${
              /^\d{1,3}$/.test(draft.signOut.trim()) && minutes <= MAX_STATION_AUTO_SIGN_OUT_MINUTES
                ? signOutText(minutes)
                : '—'
            }.`}
          >
            <Input
              data-field="signOut"
              inputMode="numeric"
              autoComplete="off"
              className="w-32 tabular-nums"
              value={draft.signOut}
              onChange={set('signOut')}
            />
          </FormField>
        </Section>
      </fieldset>

      {failed && (
        <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
          {failed.explain} Your changes are still in the form.
        </Notice>
      )}
      {canEdit && (
        // Stays in view while the form scrolls (UI4A-10).
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-end gap-3 border-t border-border bg-surface py-3">
          {dirty && (
            <p className="mr-auto text-sm text-muted">You have changes that aren’t saved.</p>
          )}
          <Button
            type="button"
            variant="secondary"
            icon={<Undo2 aria-hidden />}
            disabled={!dirty || save.isPending}
            onClick={() => {
              setDraft(toDraft(saved));
              setErrors({});
            }}
          >
            Undo changes
          </Button>
          <Button type="submit" icon={<Save aria-hidden />} loading={save.isPending}>
            Save
          </Button>
        </div>
      )}
    </form>
  );
}

function StockControlSettingsPage() {
  useDocumentTitle('Stock control settings');
  const q = useQuery(stockControlSettingsQuery());
  const { data: me } = useQuery(meQueryOptions());
  const canEdit =
    me !== undefined && me.permissions.includes('writeoff.approve') && me.branch_scope === null;

  let body: ReactNode;
  if (q.isError && !q.data) {
    body = (
      <ErrorPanel
        error={q.error}
        titleAs="h2"
        what="The stock control settings"
        onRetry={() => void q.refetch()}
      />
    );
  } else if (!q.data || !me) {
    body = <LoadingSkeleton variant="form" label="Loading the settings" />;
  } else {
    body = (
      <div className="flex flex-col gap-5">
        {!canEdit && (
          <Notice tone="neutral" title="You can read these, not change them">
            Changing them needs the “approve write-offs” permission and access to every branch. Ask
            the owner.
          </Notice>
        )}
        {/* Keyed on the saved values: a save elsewhere resets the form. */}
        <SettingsForm key={JSON.stringify(q.data)} saved={q.data} canEdit={canEdit} />
      </div>
    );
  }

  return (
    <SettingsFrame>
      <PageHeader
        eyebrow={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/settings">Settings</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Stock control</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
        title="Stock control"
        description="When counts and write-offs need a manager, when the daily expiry list is made, and when stations sign people out."
      />
      {body}
    </SettingsFrame>
  );
}
